import React, { useEffect, useLayoutEffect, useMemo, type Ref } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';
import Button from '@atlaskit/button/new';
import UploadIcon from '@atlaskit/icon/core/cloud-arrow-up';
import type { UploadsStartEventPayload, MediaFile } from '@atlaskit/media-picker';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { DEFAULT_SCROLL_OPTIONS } from '@atlassian/jira-issue-create-common-types/src/common/constants/index.tsx';
import { AsyncAttachmentsFilmstripView } from '@atlassian/jira-issue-field-attachment/src/async.tsx';
import useAttachments from '@atlassian/jira-issue-field-attachment/src/services/use-attachments/index.tsx';
import type { TemporaryAttachment } from '@atlassian/jira-issue-field-attachment/src/services/use-attachments/types.tsx';
import AttachmentsUpload from '@atlassian/jira-issue-field-attachment/src/ui/attachments-upload/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useShouldBlockStorageUploads } from '@atlassian/jira-storage-hard-limits/src/services/use-should-block-storage-uploads/useShouldBlockStorageUploads.tsx';
import { jiraStorageHardLimitsPopupModalEntryPoint } from '@atlassian/jira-storage-hard-limits/src/ui/entrypoint.tsx';
import { ModalTrigger } from '@atlassian/entry-point-modal-trigger';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	type ApplicationKey,
	SOFTWARE,
} from '@atlassian/jira-shared-types/src/application-key.tsx';
import type { StorageHardLimitsModalProps } from '@atlassian/jira-storage-hard-limits/src/ui/type.tsx';
import { STORAGE_HARD_LIMITS_MODAL_IDS } from '@atlassian/jira-storage-hard-limits/src/ui/constants.tsx';
import { attachmentDropzoneStorageHardLimitsModalEntryPoint } from '@atlassian/jira-storage-hard-limits/src/ui/drop-zone-modal/entrypoint.tsx';
import { useStorageHardLimitsModalOnClose } from '@atlassian/jira-storage-hard-limits/src/services/use-storage-hard-limits-modal-onclose/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container/src/index.tsx';

import { withFormField } from '../../common/ui/with-form-field/index.tsx';
import messages from './messages.tsx';
import type { Props, FieldValue, AttachmentFieldConfig } from './types.tsx';
import { validateAttachment, getFieldDropZoneContainerID } from './utils.tsx';

export const AttachmentsField = (props: Props): React.JSX.Element | null => {
	const {
		mediaUploadContext,
		onInProgressChange,
		projectId,
		fieldId = '',
		fieldProps: { onChange, value, ...fieldProps },
		maxAllowedTotalAttachmentsSize,
		onAttachmentSizeValidation,
		dropzoneContainerId = '',
		shouldScrollIntoViewOnUploadStart = false,
		defaultValue = [],
		attachmentCustomUploadURL,
		storageLimitsData,
	} = props;

	const { formatMessage } = useIntl();
	const {
		files,
		onUploadStart,
		onUploadError,
		mediaClientConfig,
		uploadConfig,
		onRemoveFile,
		onUploadEnd,
		isUploading,
		setAttachments,
		...resUseAttachments
	} = useAttachments({
		mediaUploadContext,
		projectId,
		maxAllowedTotalAttachmentsSize,
		onAttachmentSizeValidation,
		attachmentCustomUploadURL,
	});

	const { isDefaultValueInitialized, setDefaultValueInitialized } = resUseAttachments;

	useEffect(() => {
		// setting DefaultValueInitialized only when receiving default Value to guarantee we will set DefaultValue even if it's loaded with some delay.
		if (defaultValue && Array.isArray(defaultValue) && isDefaultValueInitialized === false) {
			const currentAttachments: TemporaryAttachment[] = [];
			setAttachments(
				currentAttachments.concat(
					// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
					defaultValue.map((mediaFile: MediaFile) => ({
						mediaFile,
						isUploading: false,
					})),
				),
			);
			setDefaultValueInitialized(true);
		}
	}, [setAttachments, defaultValue, isDefaultValueInitialized, setDefaultValueInitialized]);

	if (fg('gic-attachment-field-with-react18-concurrent-mode')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useLayoutEffect(() => {
			let shouldCallOnChange = false;
			const isValueAnArray = Array.isArray(value);

			if (isValueAnArray && value.length !== files.length) {
				shouldCallOnChange = true;
			}

			if (!isValueAnArray && files.length) {
				shouldCallOnChange = true;
			}

			if (shouldCallOnChange) {
				onChange(files);
			}
		}, [files, onChange, value]);
	} else {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			let shouldCallOnChange = false;
			const isValueAnArray = Array.isArray(value);

			if (isValueAnArray && value.length !== files.length) {
				shouldCallOnChange = true;
			}

			if (!isValueAnArray && files.length) {
				shouldCallOnChange = true;
			}

			if (shouldCallOnChange) {
				// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
				onChange(files);
			}
		}, [files, onChange, value]);
	}

	// TBD: create a controlled reusable attachment component
	useEffect(() => {
		// @ts-expect-error - TS2339 - Property 'shouldClear' does not exist on type 'FieldValue'.
		if (value && value.shouldClear === true) {
			setAttachments([]);
		}
	}, [setAttachments, value]);

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
		onInProgressChange && onInProgressChange(isUploading);
	}, [isUploading, onInProgressChange]);

	const shouldBlockStorageUploads = fg('jira-storage-hard-limit-gate')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useShouldBlockStorageUploads({
				attachmentStorageAllowed: storageLimitsData?.attachmentStorageAllowed ?? undefined,
				attachmentStorageUsed: storageLimitsData?.attachmentStorageUsed ?? undefined,
				attachmentStorageIsUnlimited: storageLimitsData?.attachmentStorageIsUnlimited ?? undefined,
			})
		: false;

	const cloudId = useCloudId();
	const dialogId = STORAGE_HARD_LIMITS_MODAL_IDS.ISSUE_CREATE_ATTACHMENTS_FIELD;
	const { handleClose } = useStorageHardLimitsModalOnClose(dialogId);
	const entryPointParams = useMemo(
		(): {
			cloudId: string;
			applicationKey: ApplicationKey;
		} => ({
			cloudId,
			applicationKey: SOFTWARE,
		}),
		[cloudId],
	);

	const entryPointProps: StorageHardLimitsModalProps = useMemo(
		() => ({ dialogId, onClose: handleClose }),
		[dialogId, handleClose],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		attachmentDropzoneStorageHardLimitsModalEntryPoint,
		entryPointParams,
	);

	if (!mediaUploadContext) {
		return null;
	}

	const onAttachmentUploadStart = (uploadStartPayload: UploadsStartEventPayload) => {
		if (shouldBlockStorageUploads && entryPointActions) {
			entryPointActions.load();
			return;
		}

		onUploadStart(uploadStartPayload);
		if (shouldScrollIntoViewOnUploadStart) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const fieldElement = document.getElementById(getFieldDropZoneContainerID(fieldId));
			if (fieldElement) {
				scrollIntoViewIfNeeded(fieldElement, DEFAULT_SCROLL_OPTIONS);
			}
		}
	};

	return (
		<>
			{shouldBlockStorageUploads && (
				<ModalEntryPointContainer
					id="create-issue-attachment-dropzone-storage-hard-limits-modal"
					entryPointReferenceSubject={entryPointReferenceSubject}
					entryPointActions={entryPointActions}
					runtimeProps={entryPointProps}
					errorFallback="unmount"
					teamName="growth-tako"
				/>
			)}
			<FieldDropzoneContainer id={getFieldDropZoneContainerID(fieldId)}>
				<BrowseDropzoneArea>
					<UploadIcon spacing="spacious" label="" color={token('color.icon')} />
					<BrowseDropzoneAreaText>
						{`${formatMessage(messages.dropzone)}`}&nbsp;
					</BrowseDropzoneAreaText>
					<AttachmentsUpload
						{...fieldProps}
						mediaClientConfig={mediaClientConfig}
						uploadConfig={uploadConfig}
						onUploadError={shouldBlockStorageUploads ? noop : onUploadError}
						onUploadStart={onAttachmentUploadStart}
						onUploadEnd={shouldBlockStorageUploads ? noop : onUploadEnd}
						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						dropzoneContainer={document.getElementById(
							dropzoneContainerId || getFieldDropZoneContainerID(fieldId),
						)}
					>
						{({ setIsBrowserOpen }) => (
							<Box xcss={browseButtonStyles}>
								{shouldBlockStorageUploads ? (
									<ModalTrigger
										entryPoint={jiraStorageHardLimitsPopupModalEntryPoint}
										entryPointParams={entryPointParams}
										entryPointProps={entryPointProps}
									>
										{({ ref }) => (
											<Button
												// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
												ref={ref as Ref<HTMLButtonElement>}
												aria-label={formatMessage(messages.browseAriaLabel)}
											>
												{formatMessage(messages.browse)}
											</Button>
										)}
									</ModalTrigger>
								) : (
									<Button
										onClick={() => setIsBrowserOpen(true)}
										aria-label={formatMessage(messages.browseAriaLabel)}
									>
										{formatMessage(messages.browse)}
									</Button>
								)}
							</Box>
						)}
					</AttachmentsUpload>
				</BrowseDropzoneArea>
			</FieldDropzoneContainer>
			{files.length > 0 && (
				<Box xcss={mediaContainerStyles}>
					<Placeholder name="attachments-filmstrip-view" fallback={null}>
						<AsyncAttachmentsFilmstripView
							mediaClientConfig={mediaClientConfig}
							files={files}
							onRemoveFile={onRemoveFile}
							collectionName={mediaUploadContext.collection}
						/>
					</Placeholder>
				</Box>
			)}
		</>
	);
};

export default withFormField({
	validator: validateAttachment,
	// @ts-expect-error - TS2345 - Argument of type '(props: Props) => JSX.Element | null' is not assignable to parameter of type 'AbstractComponent<ComponentProps<AttachmentFieldConfig, FieldValue>, any>'.
})<AttachmentFieldConfig, FieldValue>(AttachmentsField);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldDropzoneContainer = styled.div({
	display: 'flex',
	justifyContent: 'center',
	borderWidth: token('border.width'),
	borderStyle: 'dashed',

	borderColor: `${token('color.border')}`,
	paddingTop: token('space.150'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),

	color: token('color.text.subtlest'),
});

const mediaContainerStyles = xcss({
	marginTop: 'space.050',
});

const browseButtonStyles = xcss({
	marginLeft: 'space.075',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BrowseDropzoneArea = styled.span({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BrowseDropzoneAreaText = styled.span({
	marginLeft: token('space.050'),
});
